/**
 * ProductCardCompact Component
 *
 * Horizontal list view variant of ProductCard for compact display.
 * Shows essential info: name, category, price, weight, tier badge, and actions.
 */

import type { SelectedProduct } from '@nfa/types';
import { trackAffiliateClick } from '../lib/analytics';
import { getAmazonProductUrl } from '../lib/amazon';

interface ProductCardCompactProps {
  product: SelectedProduct;
  onSwap?: (categoryId: string, alternativeProductId: string) => void;
  onToggleOwned?: (productId: string) => void;
  isOwned?: boolean;
  formatPrice?: (gbpValue: number) => string;
}

function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

function getTierBadge(tier: SelectedProduct['tier']) {
  switch (tier) {
    case 'budget':
      return (
        <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">
          Budget
        </span>
      );
    case 'premium':
      return (
        <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded">
          Premium
        </span>
      );
    default:
      return null;
  }
}

export function ProductCardCompact({
  product,
  onToggleOwned,
  isOwned = false,
  formatPrice,
}: ProductCardCompactProps) {
  const displayPrice = formatPrice || ((gbp: number) => `£${gbp}`);

  // Determine primary link and retailer
  // For Amazon, ensure the affiliate tag is always present
  const { primaryLink, retailer } = (() => {
    const amazonUrl = getAmazonProductUrl(product.affiliate_links);
    if (amazonUrl) return { primaryLink: amazonUrl, retailer: 'amazon' };
    if (product.affiliate_links?.cotswold) return { primaryLink: product.affiliate_links.cotswold, retailer: 'cotswold' };
    if (product.affiliate_links?.alpkit) return { primaryLink: product.affiliate_links.alpkit, retailer: 'alpkit' };
    if (product.affiliate_links?.decathlon) return { primaryLink: product.affiliate_links.decathlon, retailer: 'decathlon' };
    return { primaryLink: null, retailer: '' };
  })();

  const handleBuyClick = () => {
    trackAffiliateClick(product.product_id, retailer);
  };

  const handleToggleOwned = () => {
    if (onToggleOwned) {
      onToggleOwned(product.product_id);
    }
  };

  return (
    <div
      className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg transition-colors ${
        isOwned
          ? 'bg-green-500/5 border border-green-500/30'
          : 'bg-neutral-900 border border-neutral-800 hover:border-neutral-700'
      }`}
    >
      {/* Product Info */}
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
        {/* Name & Category */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-white text-sm sm:text-base truncate">
              {product.product_name}
            </h3>
            {getTierBadge(product.tier)}
            {product.style === 'ultralight' && (
              <span className="px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 text-xs rounded">
                UL
              </span>
            )}
            {isOwned && (
              <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">
                Owned
              </span>
            )}
          </div>
          <p className="text-xs text-neutral-500 truncate">
            {product.category_name}
          </p>
        </div>

        {/* Price & Weight */}
        <div className="flex items-center gap-4 shrink-0">
          <span className="font-mono text-sm sm:text-base font-medium text-white">
            {displayPrice(product.price_gbp)}
          </span>
          <span className="font-mono text-xs sm:text-sm text-neutral-500 w-14 text-right">
            {formatWeight(product.weight_g)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Own Toggle */}
        {onToggleOwned && (
          <button
            onClick={handleToggleOwned}
            className={`p-2 rounded-md transition-colors ${
              isOwned
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                : 'bg-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-700'
            }`}
            aria-label={isOwned ? 'Mark as not owned' : 'Mark as owned'}
          >
            <svg className="w-4 h-4" fill={isOwned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
        )}

        {/* Buy Button */}
        {primaryLink && !isOwned && (
          <a
            href={primaryLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleBuyClick}
            className="p-2 bg-neutral-800 hover:bg-orange-500 text-neutral-300 hover:text-black rounded-md transition-colors"
            aria-label={`Buy ${product.product_name}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}
